import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { organizationsGetBusinessInformation } from "~/api-refs";
import { PageWrapper } from "~/components/page-wrapper";
import { BusinessInformationCard } from "~/components/settings/business-information-card";
import { FormSkeleton } from "~/components/skeletons/form-skeleton";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";

export const Route = createFileRoute(
  "/_authenticated/settings/business-information"
)({
  component: BusinessInformationPage,
  pendingComponent: () => (
    <FormSkeleton
      description="Manage your business information"
      title="Business Information"
    />
  ),
});

function BusinessInformationPage() {
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  const { data: businessData, isLoading } = useQuery({
    ...convexQuery(organizationsGetBusinessInformation, {}),
    enabled: canViewIntegrations,
  });

  if (permissionsLoading || isLoading) {
    return (
      <FormSkeleton
        description="Manage your business information"
        title="Business Information"
      />
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="Manage your business information"
        title="Business Information"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view business information
  if (!canViewIntegrations) {
    return (
      <PageWrapper
        description="Manage your business information"
        title="Business Information"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view business information. Contact your
            manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // Handle case where organization exists but has no data yet
  const data = businessData ?? {
    businessName: "",
    businessEmail: "",
    phoneNumber: "",
    onboardingComplete: false,
    useStripeAccountsV2: false,
    stripeAccountId: undefined,
  };

  return (
    <PageWrapper
      description="Manage your business information"
      title="Business Information"
    >
      <div className="flex flex-col gap-5">
        <BusinessInformationCard businessData={data} type="businessName" />
        <BusinessInformationCard businessData={data} type="phoneNumber" />
      </div>
      {/* V4.1: Outlet for child routes (e.g., /edit/$type modal) */}
      <Outlet />
    </PageWrapper>
  );
}
